import React from "react";

import { useAppState } from "hooks/AppContext";
import { MetadataObj } from "utils/objects";
import { ArticleCompanySponsoredWidgetProps, ArticleCompanySponsor } from "utils/orm";
import { getId } from "utils/strings";
import { Tag } from "utils/tags";
import { Link } from "components/Link";
import { RichTextModule } from "components/RichTextModule";
import { ResponsiveImage } from "components/ResponsiveImage";
import { SponsoredRecircWidget } from "components/SponsoredRecircWidget";

import styles from "./sponsorModule.module.scss";

type SponsorType = "sidebar" | "midway" | "footer";
type AltType = "profile" | "culture" | "working";

export interface SponsorProps extends ArticleCompanySponsor {
    listicleContentType: string;
    size?: number;
    tags: Tag[];
    sponsorType?: SponsorType;
    componentProps?: {
        sponsorWidgetProps: ArticleCompanySponsoredWidgetProps
    };
}

interface SponsorTypeProps {
    active: boolean;
    companyName: string;
    description: string;
    legacyId: string;
    logoImage: string;
    primaryImage: string;
    secondaryImage: string;
    shortName: string;
    sponsorName: string;
    cta: string;
    size?: number;
    tertiaryImage: string;
    sponsorType: SponsorType;
    componentProps?: {
        sponsorWidgetProps: ArticleCompanySponsoredWidgetProps
    };
}

interface LinkProps {
    companyName: string;
    cta?: string;
    size: number;
    url: string;
    trackingData?: MetadataObj;
}

interface Placement {
    footer: boolean;
    sidebar: boolean;
    inline: boolean;
    bio: boolean;
}

interface SponsorPlacement {
    active: boolean;
    product: string;
    hasSponsorShip: boolean;
}

/**
 * Return an object with all the possible sponsor placements and if they are valid.
 */
export const getSponsorPlacement = (props: SponsorPlacement): Placement => {
    const launchReg = new RegExp("launch", "i");
    const roundupReg = new RegExp("roundup", "i");
    const sponsoredArticleReg = new RegExp("sponsored-article", "i");
    const existingReg = new RegExp("existing-article-sponsorship", "i");
    const tierReg = new RegExp("tier", "i");
    const isActive = props.active;
    const isEmpty = props.product === "";
    const isExisting = existingReg.test(props.product);
    const isLaunch = launchReg.test(props.product);
    const isRoundup = roundupReg.test(props.product);
    const isSponsored = props.hasSponsorShip;
    const isSponsoredArticle = sponsoredArticleReg.test(props.product);
    const isTier = tierReg.test(props.product);
    const placement = {
        footer: [isSponsored, isActive, !isRoundup, !isEmpty].every(Boolean),
        sidebar: [isSponsored, isActive, !isRoundup, !isEmpty].every(Boolean),
        inline: [isSponsored, isActive, !isLaunch, !isEmpty].every(Boolean),
        bio: [
            [isSponsored, isActive, !isTier, !isLaunch, !isExisting].every(Boolean),
            [isSponsored, !isActive, !isTier, !isRoundup, !isSponsoredArticle, !isEmpty, !isExisting].every(Boolean)
        ].some(Boolean)
    };
    return placement;
};

/**
 * Generates link and responsive image for the company logo.
 */
const Logo: React.FC<LinkProps> = (props) => {
    const {
        cta, companyName, url, size, trackingData
    } = props;
    return (
        <a className={styles.logoWrapper} href={cta} target="_blank" rel="noreferrer">
            <ResponsiveImage
                loading="lazy"
                altText={`Sponsored by ${companyName}`}
                url={url}
                mode="clip"
                mobileWidth={size * 2}
                mobileHeight={size * 2}
                extraProps={trackingData}
            />
        </a>
    );
};

/**
 * Generates the Sponsor Sidebar sometimes called the banner variant.
 */
const SponsorRecircSidebar: React.FC<SponsorTypeProps> = (props) => {
    const { componentProps } = props;
    const { sponsorWidgetProps } = componentProps;

    return <SponsoredRecircWidget {...sponsorWidgetProps} />;
};

/**
 * Generates the Sponsor Footer
 */
const SponsorRecircFooter: React.FC<SponsorTypeProps> = (props) => {
    const { componentProps } = props;
    const { sponsorWidgetProps } = componentProps;

    return <SponsoredRecircWidget {...sponsorWidgetProps} />;
};

/**
 * Generates the Sponsor Midway sometimes called the BIO variant.
 */
const SponsorBio: React.FC<SponsorTypeProps> = (props) => {
    const {
        shortName, companyName, logoImage, legacyId, description, cta
    } = props;

    const { article } = useAppState();

    const LinkProps = {
        className: styles.callToAction,
        href: cta,
    };

    return (
        <div className={styles.iconMediaBlock}>
            <Logo {...{
                cta,
                shortName,
                companyName,
                url: logoImage,
                size: 75,
                trackingData: {
                    "data-tracking-event-type": "click",
                    "data-tracking-props": JSON.stringify({
                        sp_click_component: "insert",
                        sp_click_component_tile_type: "company_description",
                        sp_click_position: "footer",
                        sp_click_insert_type: "logo",
                        sp_click_target_page_position: "profile",
                        sp_page_type: "content",
                        sp_page_section: "advice",
                        sp_page_company_id: getId(legacyId).toString(),
                        sp_page_entity_id: article.id.toString() || null
                    })
                }
            }}
            />
            <div className={styles.content}>
                <h3>
                    Sponsored by&nbsp;
                    {companyName}
                </h3>
                <RichTextModule type="richTextBlock" richText={description} />
                <Link {...LinkProps}>
                    See Company Profile
                    <span className="fa fa-angle-right" />
                </Link>
            </div>
        </div>
    );
};

/**
 * Creates one of the 3 sponsor Elements: sidebar, midway, footer
 */
const SponsorElement: React.FC<SponsorProps> = ({
    componentProps,
    active,
    sponsorLink,
    shortName,
    sponsorDescription,
    description,
    primaryImage,
    secondaryImage,
    tertiaryImage,
    logoImage,
    companyName,
    sponsorName,
    legacyId,
    size,
    sponsorType,
    id
}) => {
    let moduleTrackingProps = {};
    const cleanProps = {
        active,
        cta: sponsorLink || `/profiles/${shortName}`,
        description: sponsorDescription || description,
        primaryImage,
        secondaryImage,
        tertiaryImage,
        logoImage,
        companyName,
        sponsorName,
        legacyId,
        shortName,
        size,
        sponsorType,
        id,
        componentProps
    };

    if (sponsorType === "midway") {
        moduleTrackingProps = {
            "data-tracking-event-type": "impression",
            "data-tracking-props": JSON.stringify({
                sp_impression_component: "tile",
                sp_impression_tile_type: "company_description",
                sp_impression_position: "footer",
                sp_content_is_tile: false,
                sp_tile_entity_id: getId(legacyId).toString(),
                sp_tile_company_id: getId(legacyId).toString(),
            })
        };
    } else if (sponsorType === "sidebar" || sponsorType === "footer") {
        moduleTrackingProps = {
            "data-tracking-event-type": "impression",
            "data-tracking-props": JSON.stringify({
                sp_impression_component: "insert",
                sp_impression_tile_type: null,
                sp_impression_position: "right_rail",
                sp_impression_insert_type: "sponsor_company_jobs",
                sp_content_is_tile: false,
                sp_tile_entity_id: null,
            })
        };
    }

    const decisionTree = {
        sidebar: <SponsorRecircSidebar {...{ ...cleanProps }} />,
        midway: <SponsorBio {...{ ...cleanProps }} />,
        footer: <SponsorRecircFooter {...{ ...cleanProps }} />
    };

    return (
        <aside className={styles[sponsorType]} {...moduleTrackingProps}>{decisionTree[sponsorType]}</aside>
    );
};

/**
 * Sponsor module business logic: which and if a module should load.
 * See business logic and layout at https://themuse.atlassian.net/l/c/XLUqU116
 * @example
 * import { SponsorModule } from "components/SponsorModule";
 * <SponsorModule {...{ type: "sidebar", ...props }} />
 * <SponsorModule {...{ type: "midway", ...props }} />
 * <SponsorModule {...{ type: "footer", ...props }} />
 */
export const SponsorModule: React.FC<SponsorProps> = (props) => {
    const {
        active, hasSponsorShip, product, sponsorType
    } = props;
    const placements = getSponsorPlacement({ active, hasSponsorShip, product });
    const { footer, sidebar, bio } = placements;

    const decisionTree = {
        footer: () => (footer ? <SponsorElement {...props} /> : null),
        sidebar: () => (sidebar ? <SponsorElement {...props} /> : null),
        midway: () => (bio ? <SponsorElement {...props} /> : null)
    };

    return decisionTree[sponsorType] ? decisionTree[sponsorType]() : null;
};
